/**
 * 인연의 보금자리 — 포켓몬 교환 게시판 (2026-09-21).
 *
 * **「보금자리」 탭의 하위탭 하나**다 (2026-09-21 개편). 방(PSRoom)이 아니라 순수 컴포넌트라
 * `CherishNestPanel` 이 자기 자리에 그려 넣는다 — 탭 안의 탭이므로 방을 따로 만들 이유가 없다.
 *
 * 처음엔 배틀홈 카드 → 독립 방이었다. 상단 탭바가 4칸 한계라 새 탭을 못 만든다고 봤는데,
 * **도전과제를 랭킹 하위로 내려** 자리를 만들었다 (홈이 섹션 17개로 불어난 것도 같이 푼다).
 *
 * ⚠️ **화면 이름은 「인연의 보금자리」 하나뿐이다.** 「시장」·「거래」·「매물」·「등록」 같은
 *    말을 문구에 쓰지 말 것 — 아이가 오가는 자리의 어휘는 입양·가족이다
 *    (경매 = 「특별한 보금자리」와 같은 결).
 *
 * ⚠️ **자유 문구 입력구를 만들지 말 것.** 여기 있는 입력은 종 검색 한 칸뿐이고 그건 서버로
 *    안 간다(받아 둔 목록을 메모리에서 거르는 용도). 글에 담기는 건 종·특성·체크박스·개체
 *    id 뿐이다. 한 칸이라도 열면 「체리 5만 주시면 드려요」가 올라온다.
 *
 * ⚠️ **타이머를 걸지 않는다** (2026-09-19 통로 하나 규약). 게시판은 초 단위로 바뀌지 않는다.
 *    들어올 때·탭으로 돌아올 때·내가 뭔가 한 뒤에만 다시 받는다. `setInterval` 금지.
 *
 * ⚠️ `async/await` 를 쓰지 않는다 (이 포크 클라 규약). fetch 는 `.then()` 체인으로.
 */
import preact from "../js/lib/preact";
import { PS, type RoomID } from "./client-main";

const CWM_API = 'https://cherish-world.com/api/showdown/market';
/** 다시 볼 때 이보다 오래된 자료면 새로 받는다 (고정 탭들과 같은 10초 규칙). */
const CWM_STALE_MS = 10000;

// ---- 서버가 주는 모양 -------------------------------------------------------

interface CwmMon {
	id: string;
	speciesId: string;
	koName: string | null;
	speciesName: string;
	spriteId: string;
	nickname: string | null;
	abilityKo: string;
	abilityHidden: boolean;
	shiny: boolean;
	gender: string;
	customFormId: string | null;
	bagCount?: number;
	originalOwnerName?: string | null;
}
/** notAtHome — 「우리 집에 없는 아이」 배지 (2026-09-30). 옛 API 면 없다. */
interface CwmOffer { mon: CwmMon; lockReason: string | null; notAtHome?: boolean }
interface CwmListing {
	id: string;
	kind: string;
	status: string;
	ownerUserId: string;
	ownerName: string;
	mine: boolean;
	want: {
		speciesId: string; ko: string; spriteId: string;
		abilitiesKo: string[]; shinyOnly: boolean; genderKo: string | null;
		/** 진화 전 아이도 받는 글인가 (2026-09-23). */
		includePrevo?: boolean;
		/** 같이 받는 조상들의 이름. */
		prevoKo?: string[];
	} | null;
	offers: CwmOffer[];
	/**
	 * 이 글에 보낼 수 있는 내 아이들의 개체 id (잠긴 아이 포함) — **서버 판정**이다.
	 * 전에는 클라가 종·특성·이로치·성별을 제 손으로 다시 대조했는데, 특성을 한글 이름으로
	 * 비교하고 있어 진화 전 아이(특성이 다르다)가 들어오는 순간 틀린 답을 냈다.
	 */
	matchMonIds?: string[];
	anyOfferReady: boolean;
	iCanMatch: boolean;
	blockedOnly: boolean;
	createdAt: string;
	expiresAt: string | null;
}
interface CwmSpecies {
	id: string; ko: string;
	/** 그림 파일 이름 — **종 id 와 다르다** (ninetalesalola → ninetales-alola). */
	spriteId?: string;
	/** 진화 전 아이가 있는 종인가 — 없으면 체크칸을 아예 안 그린다. */
	hasPrevo?: boolean;
	abilities: { id: string, ko: string, hidden: boolean }[];
}
interface CwmMonOption { mon: CwmMon; lockReason: string | null }
interface CwmMe {
	open: boolean;
	giveOpen: boolean;
	listingFee: number;
	offerMax: number;
	acceptsLeft: number | null;
	/** 오늘 남은 분양 응모 횟수 (null = 무제한이거나 분양이 닫힘). */
	entriesLeft?: number | null;
	myListing: CwmListing | null;
	/** 내가 응모한 분양 글과 그 결과 (2026-09-22). */
	myEntries?: CwmEntry[];
}

/**
 * 「내가 응모한 아이」 — 응모하면 버튼이 「응모했어요」로 바뀌고 끝이라, 떨어지면 글이 그냥
 * 사라져 **내가 진 건지 글이 닫힌 건지 몰랐다**. 탈락 DM 은 인원이 늘면 대량 발송이 되므로
 * (봇A 소프트밴 전례) 화면에만 남긴다.
 */
interface CwmEntry {
	listingId: string;
	outcome: 'waiting' | 'queued' | 'won' | 'open' | 'taken' | 'lost' | 'gone';
	drawRank: number | null;
	mon: CwmMon | null;
	ownerName: string;
	dueInMs: number | null;
	createdAt: string;
}

/** 결과 한 줄. ⚠️ 탈락은 **에둘러 말하지 않는다** — 「아직 기회가 있나」가 한눈에 보여야 한다. */
const CWM_ENTRY_KO: { [k in CwmEntry['outcome']]: string } = {
	waiting: '발표를 기다리는 중이에요',
	queued: '앞 순번이 먼저 데려갈 차례예요',
	won: '당첨됐어요! 「무료분양」 탭에서 데려가 주세요',
	open: '이제 누구나 데려갈 수 있어요',
	taken: '데려왔어요',
	lost: '아쉽게도 당첨 되지 않았어요',
	gone: '글이 내려갔어요',
};

/**
 * 받기 전에 반드시 알려야 하는 두 가지 (2026-09-22 사용자 지시).
 *
 * 둘 다 **서버가 막는 규칙**인데 화면이 말을 안 해서, 받고 나서야 알게 됐다.
 *   - 이름: `originalOwnerId` 가 남에게 있으면 별명 변경이 거부된다 (pokemon-nickname.ts)
 *   - 교환: 보금자리로 온 아이는 `obtained_via` 가 `market:` 이 되어 **영구 교환 불가**다
 *     (assertTradable). 7일 쿨타임이 아니라 스타터·경매와 같은 무리다.
 *
 * ⚠️ 「영구」라고 분명히 쓴다 — 되돌릴 수 없는 규칙이라 에둘러 말하면 나중에 항의가 된다.
 */
function cwmTakeNotice() {
	return <>
		데려온 아이는 <b>다시 교환하거나 보금자리에 올릴 수 없어요.</b> 새 가족과 쭉 함께예요.
		{' '}이름도 바꿀 수 없어요 — 첫 보호자가 지어준 이름 그대로 지내요.
	</>;
}

/** 올릴 수 없는 사유 → 화면 문구. 서버 `MarketLockReason` 과 한 벌로 움직인다. */
const CWM_LOCK_KO: { [k: string]: string } = {
	starter: '첫 파트너예요',
	auction: '특별한 보금자리에서 온 아이예요',
	awakened: '특별한 물건을 쓴 아이예요',
	partner: '파트너예요',
	rented: '다른 집에 가 있어요',
	daycare: '어린이집에 있어요',
	love: '온실에 있어요',
	market_give: '새 가족을 찾고 있어요',
	market: '보금자리에서 만난 아이예요',
	retrade_lock: '온 지 얼마 안 됐어요',
};

// ---- 한글 검색 --------------------------------------------------------------

const CWM_CHO = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ',
	'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];

/**
 * 초성 뽑기 — 「ㅍㅋㅊ」로 피카츄를 찾게 한다.
 *
 * 폰에서 특히 값이 크다. 이름 철자가 헷갈리는 종(딱충이·독침붕)에서 오타로 못 찾고
 * 포기하는 걸 막는 게 목적이고, 결과를 그림 칩으로 보여 주는 것이 그 짝이다.
 */
function cwmChoseong(s: string): string {
	let out = '';
	for (let i = 0; i < s.length; i++) {
		const code = s.charCodeAt(i) - 0xac00;
		out += code >= 0 && code <= 11171 ? CWM_CHO[Math.floor(code / 588)] : s[i];
	}
	return out;
}

function cwmMatches(sp: CwmSpecies, q: string): boolean {
	const needle = q.replace(/\s+/g, '');
	if (!needle) return false;
	if (sp.ko.replace(/\s+/g, '').indexOf(needle) >= 0) return true;
	// 입력이 초성만으로 이뤄졌을 때만 초성 대조를 한다 (그래야 「가」가 ㄱ 전체를 안 물어온다)
	// ⚠️ 이름에서 띄어쓰기를 **먼저 뺀다** — 폼 이름이 「알로라 나인테일」이라 그대로 두면
	//    「ㅇㄹㄹㄴㅇㅌㅇ」가 공백에 걸려 안 잡힌다 (2026-09-23 폼 표기와 함께 들어온 함정).
	if (/^[ㄱ-ㅎ]+$/.test(needle)) return cwmChoseong(sp.ko.replace(/\s+/g, '')).indexOf(needle) >= 0;
	return sp.id.toLowerCase().indexOf(needle.toLowerCase()) >= 0;
}

// ---- 그림 -------------------------------------------------------------------

/**
 * 그림 — 「내 포켓몬」과 같은 규칙(patch-custom-form.py 의 cherishMonArt)을 쓴다 (2026-10-01).
 * 처음엔 쇼다운 기본 도트(sprites/gen5, 96px)를 써서 이 화면만 저화질이었다(사용자 지적).
 *   특별폼  /cherish/form/<폼>/<그림>.png  → 없으면 보통 고화질로
 *   보통    /cherish/sprite/[shiny/]<그림>.png (공식 일러스트 320px) → 없으면 쇼다운 도트로
 * 대체는 한 단계씩, `data-cwfb` 로 받는다 — 목록이 루프 안에서 그려져 클로저를 만들지 않는
 * 모듈 고정 함수로 둔다(빌드 babel 의 루프 변수 캡처 제약).
 */
function cwmHq(spriteId: string, shiny: boolean): string {
	return `/cherish/sprite/${shiny ? 'shiny/' : ''}${spriteId}.png?v=2`;
}
function cwmDot(spriteId: string, shiny: boolean): string {
	return `https://play.pokemonshowdown.com/sprites/gen5${shiny ? '-shiny' : ''}/${spriteId}.png`;
}
function cwmSprite(mon: { spriteId: string, shiny: boolean, customFormId: string | null }): string {
	if (mon.customFormId) return `/cherish/form/${mon.customFormId}/${mon.spriteId}.png?v=1`;
	return cwmHq(mon.spriteId, mon.shiny);
}
/** 대체 주소 — 특별폼이면 보통 고화질, 보통이면 도트. */
function cwmSpriteFb(mon: { spriteId: string, shiny: boolean, customFormId: string | null }): string {
	return mon.customFormId ? cwmHq(mon.spriteId, mon.shiny) : cwmDot(mon.spriteId, mon.shiny);
}
function cwmImgFb(e: any) {
	const img = e.currentTarget as HTMLImageElement;
	const next = img.getAttribute('data-cwfb') || '';
	img.removeAttribute('data-cwfb'); // 한 단계만 — 대체도 없으면 그대로 둔다
	if (next && next !== img.src) img.src = next;
}
/** 종 그림(글에 적힌 원하는 종·종 고르기 칩) — 개체가 아니라 폼·이로치 없음. */
function cwmSpeciesSprite(spriteId: string): string {
	return cwmHq(spriteId, false);
}
/** 주격 조사 — 받침 있으면 「이」, 없으면 「가」. 한글이 아닌 끝 글자는 「가」로 둔다. */
function cwmSubj(name: string): string {
	const code = name.charCodeAt(name.length - 1) - 0xac00;
	const batchim = code >= 0 && code <= 11171 && code % 28 !== 0;
	return name + (batchim ? '이' : '가');
}
function cwmMonName(mon: CwmMon): string {
	return mon.nickname || mon.koName || mon.speciesName;
}
/**
 * 종족명 — 이름표에 **별명이 걸렸을 때만** 따로 보여 준다 (2026-09-22).
 * 별명이 없으면 이름표가 이미 종족명이라 두 번 쓰면 같은 말이 겹친다.
 * 그전에는 별명만 보여서 「이 아이가 뭔지」를 알 길이 없었다 (사용자 지적).
 */
function cwmSpeciesLabel(mon: CwmMon): string | null {
	if (!mon.nickname) return null;
	return mon.koName || mon.speciesName;
}

// ---- 화면 -------------------------------------------------------------------

interface CwmGive {
	id: string;
	status: string;
	ownerUserId: string;
	ownerName: string;
	mine: boolean;
	mon: CwmMon | null;
	/** 「우리 집에 없는 아이」 배지 (2026-09-30). 옛 API 면 없다. */
	notAtHome?: boolean;
	entries: number;
	entered: boolean;
	canClaim: boolean;
	/** 발표 뒤 당첨자 — 'me' 내 차례, 'other' 다른 분 차례, null 누구나 (2026-09-24). 옛 API 면 없다. */
	winner?: 'me' | 'other' | null;
	claimInMs?: number | null;
	drawInMs: number | null;
	/** 한 번에 한 곳 (2026-09-24) — 다른 글에 응모·당첨이 걸려 있으면 여기엔 응모 못 한다. */
	enterLock?: 'entry' | 'win' | null;
	cancelable: boolean;
	createdAt: string;
}

type CwmTab = 'browse' | 'give' | 'mine';

/** 보금자리 탭의 「인연의 보금자리」 자리. 좁은 화면 여부는 부모가 알려 준다. */
export class CherishMarketView extends preact.Component<{ narrow: boolean }> {
	/**
	 * 처음 보이는 자리는 **「무료분양」**이다 (2026-09-23 사용자 지시).
	 *
	 * 「서로 보내주기」(교환)로 열리니까 「무료분양」이 있는 줄도 모르고 나가는 사람이 생겼다.
	 * 대가 없이 아이를 받아 가는 자리가 이 기능에서 제일 먼저 보여야 할 것이다.
	 *
	 * ⚠️ 분양 스위치가 꺼져 있으면 그 탭은 **버튼 자체가 안 그려진다** — 그래서 처음 값은
	 * 둘러보기로 두고, `/me` 가 「분양 열림」을 들고 온 뒤에 옮긴다. 거꾸로 하면 스위치가
	 * 꺼진 서버에서 아무 탭도 눌리지 않은 화면이 잠깐 뜬다.
	 */
	tab: CwmTab = 'browse';
	/** 사람이 탭을 한 번이라도 눌렀나 — 눌렀으면 자동으로 옮기지 않는다. */
	tabPicked = false;
	listings: CwmListing[] | null = null;
	gives: CwmGive[] | null = null;
	me: CwmMe | null = null;
	/** 기본 켜짐 — 이게 없으면 글 스무 개를 내 포켓몬 목록과 손으로 대조해야 한다. */
	/**
	 * 「우리 집 아이를 찾는 글만 보기」 — **기본 꺼짐** (2026-09-22 사용자 지시로 뒤집었다).
	 *
	 * 처음엔 켜진 채로 열었다. 글이 스무 개면 내 포켓몬 목록과 손으로 대조해야 하니까.
	 * 그런데 글이 몇 개 없는 동안은 **들어오자마자 「아직 없어요」만 보여** 자리가 빈 줄 안다.
	 * 그래서 전부 보여 주고, 거르고 싶으면 그 칸을 눈에 띄게 세워 둔다.
	 */
	matchable = false;
	loadedAt = 0;
	loading = false;
	busy = false;
	error = '';

	// 글 올리기 폼
	composing = false;
	species: CwmSpecies[] | null = null;
	query = '';
	pickedSpecies: CwmSpecies | null = null;
	pickedAbilities: { [id: string]: boolean } = {};
	wantShiny = false;
	wantGender: 'M' | 'F' | null = null;
	/** 진화 전 아이도 받을까 (2026-09-23). 종을 다시 고르면 꺼진다. */
	wantIncludePrevo = false;
	monOptions: CwmMonOption[] | null = null;
	pickedOffers: { [id: string]: boolean } = {};

	// 데려가기
	acceptFor: CwmListing | null = null;
	acceptTake: string | null = null;
	acceptGive: string | null = null;
	/** sent 가 null 이면 분양으로 받은 것 (일방 이전). */
	done: { received: CwmMon, sent: CwmMon | null } | null = null;
	// 분양 올리기
	givingUp = false;
	givePick: string | null = null;

	override componentDidMount() {
		document.addEventListener('visibilitychange', this.onVisible);
		this.refresh();
	}
	override componentWillUnmount() {
		document.removeEventListener('visibilitychange', this.onVisible);
	}
	/** 타이머 대신 이것뿐이다 — 돌아왔는데 낡았으면 한 번 받는다. */
	onVisible = () => {
		if (!document.hidden && Date.now() - this.loadedAt > CWM_STALE_MS) this.refresh();
	};

	/** 탭 고르기 — 사람이 고른 순간부터 자동 이동은 하지 않는다. */
	pickTab = (tab: CwmTab) => {
		this.tab = tab;
		this.tabPicked = true;
		this.forceUpdate();
	};

	refresh = () => {
		if (this.loading) return;
		this.loading = true;
		this.forceUpdate();
		const q = this.matchable ? '' : '?matchable=0';
		Promise.all([
			fetch(`${CWM_API}${q}`, { credentials: 'include' }).then(r => r.json()),
			fetch(`${CWM_API}/me`, { credentials: 'include' }).then(r => r.json()),
			// 분양은 스위치가 따로라 꺼져 있으면 빈 배열이 온다 — 탭도 그때 안 그린다.
			fetch(`${CWM_API}/give`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
		]).then(([list, me, give]) => {
			this.listings = (list?.data?.listings ?? []) as CwmListing[];
			this.me = (me?.data ?? null) as CwmMe | null;
			this.gives = (give?.data?.listings ?? []) as CwmGive[];
			// 분양이 열려 있으면 첫 화면을 「무료분양」으로 옮긴다 (사람이 아직 안 눌렀을 때만).
			if (!this.tabPicked && this.tab === 'browse' && this.me?.giveOpen) this.tab = 'give';
			this.error = '';
			this.loadedAt = Date.now();
		}).catch(() => {
			this.error = '보금자리에 연결하지 못했어요.';
		}).then(() => {
			this.loading = false;
			this.forceUpdate();
		});
	};

	post(path: string, body: unknown): Promise<any> {
		return fetch(`${CWM_API}${path}`, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		}).then(r => r.json()).then(j => {
			if (j?.ok === false || j?.error) {
				throw new Error(j?.error?.message || j?.message || '잠시 뒤에 다시 해주세요.');
			}
			return j?.data ?? j;
		});
	}

	// ---- 글 올리기 -----------------------------------------------------------

	openCompose = () => {
		this.composing = true;
		this.error = '';
		this.forceUpdate();
		if (!this.species) {
			// 진입 시 **한 번만** 받는다. 이후 검색은 전부 메모리에서 — 키 입력마다 서버를
			// 두드리면 통로 하나 규약에 걸린다.
			fetch(`${CWM_API}/species`, { credentials: 'include' })
				.then(r => r.json())
				.then(j => { this.species = (j?.data?.species ?? []) as CwmSpecies[]; this.forceUpdate(); })
				.catch(() => { this.error = '아이 목록을 불러오지 못했어요.'; this.forceUpdate(); });
		}
		if (!this.monOptions) {
			fetch(`${CWM_API}/mons`, { credentials: 'include' })
				.then(r => r.json())
				.then(j => { this.monOptions = (j?.data?.mons ?? []) as CwmMonOption[]; this.forceUpdate(); })
				.catch(() => { this.forceUpdate(); });
		}
	};
	closeCompose = () => { this.composing = false; this.error = ''; this.forceUpdate(); };

	onQuery = (e: Event) => {
		this.query = (e.currentTarget as HTMLInputElement).value;
		this.forceUpdate();
	};
	pickSpecies = (sp: CwmSpecies) => {
		this.pickedSpecies = sp;
		this.pickedAbilities = {};
		// 조상이 없는 종으로 갈아타면 칸이 사라지므로 값도 같이 눕힌다.
		this.wantIncludePrevo = false;
		this.query = '';
		this.forceUpdate();
	};
	toggleAbility = (id: string) => {
		this.pickedAbilities[id] = !this.pickedAbilities[id];
		this.forceUpdate();
	};
	toggleOffer = (id: string) => {
		const max = this.me?.offerMax ?? 10;
		if (!this.pickedOffers[id] && this.offerCount() >= max) return;
		this.pickedOffers[id] = !this.pickedOffers[id];
		this.forceUpdate();
	};
	offerCount(): number {
		let n = 0;
		for (const k in this.pickedOffers) if (this.pickedOffers[k]) n++;
		return n;
	}
	submitListing = () => {
		const sp = this.pickedSpecies;
		if (!sp || this.busy) return;
		const abilities: string[] = [];
		for (const a of sp.abilities) if (this.pickedAbilities[a.id]) abilities.push(a.id);
		const offerMonIds: string[] = [];
		for (const k in this.pickedOffers) if (this.pickedOffers[k]) offerMonIds.push(k);
		if (!offerMonIds.length) {
			this.error = '보내줄 수 있는 아이를 한 명은 골라주세요.';
			this.forceUpdate();
			return;
		}
		this.busy = true;
		this.error = '';
		this.forceUpdate();
		this.post('', {
			wantSpeciesId: sp.id,
			wantAbilities: abilities,
			wantShiny: this.wantShiny,
			wantGender: this.wantGender,
			wantIncludePrevo: this.wantIncludePrevo,
			offerMonIds,
		}).then(() => {
			this.composing = false;
			this.pickedSpecies = null;
			this.pickedOffers = {};
			this.pickedAbilities = {};
			this.wantShiny = false;
			this.wantGender = null;
			this.wantIncludePrevo = false;
			this.monOptions = null;
			this.tab = 'mine';
			this.tabPicked = true;
			this.refresh();
		}).catch((err: Error) => {
			this.error = err.message;
		}).then(() => {
			this.busy = false;
			this.forceUpdate();
		});
	};

	cancelListing = (id: string) => {
		if (this.busy) return;
		this.busy = true;
		this.forceUpdate();
		this.post(`/${id}/cancel`, {})
			.then(() => { this.monOptions = null; this.refresh(); })
			.catch((err: Error) => { this.error = err.message; })
			.then(() => { this.busy = false; this.forceUpdate(); });
	};

	// ---- 데려가기 ------------------------------------------------------------

	openAccept = (listing: CwmListing) => {
		this.acceptFor = listing;
		this.acceptTake = null;
		this.acceptGive = null;
		this.error = '';
		this.forceUpdate();
		if (!this.monOptions) {
			fetch(`${CWM_API}/mons`, { credentials: 'include' })
				.then(r => r.json())
				.then(j => { this.monOptions = (j?.data?.mons ?? []) as CwmMonOption[]; this.forceUpdate(); })
				.catch(() => { this.forceUpdate(); });
		}
	};
	closeAccept = () => { this.acceptFor = null; this.error = ''; this.forceUpdate(); };

	/**
	 * 이 글이 찾는 조건에 맞는 내 아이들. **잠긴 아이도 남긴다** — 빼면 어린이집에 맡긴
	 * 아이뿐인 사람에게 「지금 보낼 수 있는 아이가 없어요」만 뜨고 이유를 못 본다.
	 * 카드가 회색으로 사유를 달아 준다(`lockReason`).
	 *
	 * ⚠️ 대조는 **서버가 한다** (`matchMonIds`, 2026-09-23). 전에는 여기서 종·특성·이로치·성별을
	 * 다시 대조했는데, 특성을 **한글 이름**으로 비교하고 있어서 「진화 전 아이도 괜찮아요」가
	 * 생기자마자 틀렸다 — 랄토스의 특성 이름은 엘레이드의 것과 다르다. 규칙이 두 벌이면
	 * 반드시 갈라지므로 한 벌만 남긴다. 옛 서버가 이 칸을 안 보내면 빈 목록이 되고,
	 * 그때는 애초에 「데려가기」 버튼이 `iCanMatch` 로 잠겨 있다.
	 */
	giveCandidates(listing: CwmListing): CwmMonOption[] {
		const ids = listing.matchMonIds;
		if (!ids || !this.monOptions) return [];
		return this.monOptions.filter(o => ids.indexOf(o.mon.id) >= 0);
	}

	submitAccept = () => {
		const l = this.acceptFor;
		if (!l || !this.acceptTake || !this.acceptGive || this.busy) return;
		this.busy = true;
		this.error = '';
		this.forceUpdate();
		this.post(`/${l.id}/accept`, { takeMonId: this.acceptTake, giveMonId: this.acceptGive })
			.then((data: { received: CwmMon, sent: CwmMon }) => {
				this.acceptFor = null;
				this.done = { received: data.received, sent: data.sent };
				this.monOptions = null;
				this.refresh();
			})
			.catch((err: Error) => { this.error = err.message; })
			.then(() => { this.busy = false; this.forceUpdate(); });
	};

	// ---- 분양 ----------------------------------------------------------------

	/** 응모·수령·내리기 공용 — 끝나면 목록을 다시 받는다. */
	giveAction = (id: string, path: string) => {
		if (this.busy) return;
		this.busy = true;
		this.error = '';
		this.forceUpdate();
		this.post(`/${id}/${path}`, {})
			.then((data: any) => {
				if (data?.received) this.done = { received: data.received, sent: null };
				this.monOptions = null;
				this.loadedAt = 0;
				this.refresh();
			})
			.catch((err: Error) => { this.error = err.message; })
			.then(() => { this.busy = false; this.forceUpdate(); });
	};

	openGiveCompose = () => {
		this.givingUp = true;
		this.error = '';
		this.forceUpdate();
		if (!this.monOptions) {
			fetch(`${CWM_API}/mons`, { credentials: 'include' })
				.then(r => r.json())
				.then(j => { this.monOptions = (j?.data?.mons ?? []) as CwmMonOption[]; this.forceUpdate(); })
				.catch(() => { this.forceUpdate(); });
		}
	};

	submitGive = () => {
		if (!this.givePick || this.busy) return;
		this.busy = true;
		this.error = '';
		this.forceUpdate();
		this.post('/give', { pokemonId: this.givePick })
			.then(() => {
				this.givingUp = false;
				this.givePick = null;
				this.monOptions = null;
				this.loadedAt = 0;
				this.refresh();
			})
			.catch((err: Error) => { this.error = err.message; })
			.then(() => { this.busy = false; this.forceUpdate(); });
	};

	/** 「발표까지 6시간」 — 분 단위 아래는 굳이 안 센다. */
	giveLeftText(ms: number): string {
		const min = Math.ceil(ms / 60000);
		if (min <= 1) return '곧 발표돼요';
		if (min < 60) return `발표까지 ${min}분`;
		return `발표까지 ${Math.ceil(min / 60)}시간`;
	}

	/** 당첨자 수령 기한 — 「5시간 남았어요」. `giveLeftText` 를 쓰면 「발표까지」가 붙는다. */
	claimLeftText(ms: number): string {
		const min = Math.ceil(ms / 60000);
		if (min < 60) return `${Math.max(1, min)}분 남았어요`;
		return `${Math.ceil(min / 60)}시간 남았어요`;
	}

	/**
	 * 발표 뒤 카드 아래 한 줄 (2026-09-24). 당첨자 기한 중인 글을 남들에게 「지금 바로 데려갈 수 있어요」
	 * + 꺼진 버튼으로 보여서, 응모자 20명인 글이 「아무도 당첨 안 됐다」로 읽혔다.
	 */
	claimHint(g: CwmGive): string {
		const left = g.claimInMs !== null && g.claimInMs !== undefined ? `, ${this.claimLeftText(g.claimInMs)}` : '';
		if (g.winner === 'me') return `당첨됐어요! 데려가기를 눌러 주세요${left}`;
		if (g.winner === 'other') {
			return g.mine
				? `당첨된 분이 데려가길 기다리는 중이에요${left}`
				: `당첨된 분이 데려가길 기다리는 중이에요${left}. 안 데려가면 다음 순번에게 넘어가요`;
		}
		return '지금 바로 데려갈 수 있어요';
	}

	renderGive(g: CwmGive) {
		const waiting = g.status === 'open';
		return <div class="cwm-card" key={g.id}>
			<div class="cwm-card-head">
				{g.mon ? <img class="cwm-want-img" src={cwmSprite(g.mon)} data-cwfb={cwmSpriteFb(g.mon)} onError={cwmImgFb} alt="" /> : null}
				<div class="cwm-want">
					{/*
						당첨자가 정해진 글은 **제목 위 띠**로 먼저 갈라 준다 (2026-09-24 사용자 지시) — 아래 한 줄만
						바꿔서는 「새 가족을 찾고 있어요」 제목 + 분홍 버튼에 묻혀 여전히 누구나 받는 글로 읽혔다.
					*/}
					{g.winner === 'other' ? <span class="cwm-claim-tag">당첨자 결정, 수령 대기 중</span> : null}
					{g.winner === 'me' ? <span class="cwm-claim-tag is-me">🎉 내가 당첨됐어요</span> : null}
					{g.notAtHome ? <span class="cwm-home-tag">우리 집에 없는 아이</span> : null}
					{/*
						주인공은 아이다 (2026-09-23) — 디스코드 중계 카드와 같은 모양. 보호자 이름이 앞에
						서 있으면 「자로가 새 가족을 찾고 있어요」처럼 사람이 입양 가는 말로 읽힌다.
					*/}
					<strong>
						{g.mon
							? `${cwmSubj(cwmMonName(g.mon))} ${g.winner === 'other' ? '새 가족을 만났어요' : g.winner === 'me' ? '기다리고 있어요' : '새 가족을 찾고 있어요'}`
							: '새 가족을 찾고 있어요'}
						{g.mon?.shiny ? ' ✨' : ''}
					</strong>
					<span class="cwm-want-cond">현재 보호자 : {g.ownerName}</span>
					<span class="cwm-want-cond">
						{g.mon && cwmSpeciesLabel(g.mon) ? `${cwmSpeciesLabel(g.mon)}, ` : ''}
						{g.mon ? `${g.mon.abilityKo}${g.mon.abilityHidden ? ' (숨은 특성)' : ''}` : ''}
						{g.mon?.bagCount ? `, 도구 ${g.mon.bagCount}개 함께` : ''}
					</span>
					{g.mon?.originalOwnerName ? <span class="cwm-mon-credit">
						첫 보호자 {g.mon.originalOwnerName}
					</span> : null}
				</div>
			</div>
			<div class="cwm-card-foot">
				<span class="cwm-hint">
					{waiting && !g.mine && !g.entered && g.enterLock
						? (g.enterLock === 'win'
							? '당첨된 아이를 먼저 데려가 주세요'
							: '응모한 아이의 발표가 나면 응모할 수 있어요')
						: waiting
						? `응모 ${g.entries}명, ${this.giveLeftText(g.drawInMs ?? 0)}`
						: this.claimHint(g)}
				</span>
				{g.mine
					? <button class="cwm-btn is-quiet" disabled={this.busy || !g.cancelable}
						title={g.cancelable ? '' : '올린 지 얼마 안 됐어요'}
						onClick={() => this.giveAction(g.id, 'cancel')}>내리기</button>
					: waiting
						? <button class="cwm-btn" disabled={this.busy || g.entered || !!g.enterLock}
							onClick={() => this.giveAction(g.id, 'enter')}>{g.entered ? '응모했어요' : '응모하기'}</button>
						: <button class={`cwm-btn${g.winner === 'other' ? ' is-quiet' : ''}`} disabled={this.busy || !g.canClaim}
							onClick={() => this.giveAction(g.id, 'claim')}>{g.winner === 'other' ? '수령 대기 중' : '데려가기'}</button>}
			</div>
		</div>;
	}

	renderGiveCompose() {
		return <div class="cwm-dialog">
			<div class="cwm-dialog-in">
				<h3>어떤 아이의 새 가족을 찾을까요</h3>
				<p class="cwm-note">
					올리면 <b>그 아이는 어린이집·온실·배틀 명단에 들어갈 수 없어요.</b>
					내놓은 아이니까요. 그리고 <b>7일 동안은 다시 내릴 수 없어요.</b><br />
					24시간 뒤에 응모한 분들 중에서 추첨해요. 아무도 없으면 그때부터 누구나 데려갈 수 있어요.
				</p>
				<div class="cwm-offers is-pick">
					{!this.monOptions ? this.renderMonSkeletons() : this.monOptions.map(o => this.renderMonCard(o.mon, {
						locked: o.lockReason,
						picked: this.givePick === o.mon.id,
						onClick: () => { this.givePick = o.mon.id; this.forceUpdate(); },
					}))}
				</div>
				{this.monOptions && !this.monOptions.length
					? <p class="cwm-note">지금 보낼 수 있는 아이가 없어요.</p> : null}
				{this.error ? <p class="cwm-error" role="alert">{this.error}</p> : null}
				<div class="cwm-dialog-foot">
					<button class="cwm-btn is-quiet" onClick={() => { this.givingUp = false; this.error = ''; this.forceUpdate(); }}>그만두기</button>
					<button class="cwm-btn" disabled={!this.givePick || this.busy} onClick={this.submitGive}>올리기</button>
				</div>
			</div>
		</div>;
	}

	// ---- 렌더 조각 -----------------------------------------------------------

	/**
	 * 아이 목록이 오기 전에 깔아 두는 자리표 (2026-09-22).
	 *
	 * 왜: `/mons` 가 오기 전엔 격자가 비어 있어 창이 「제목+안내」만 한 손바닥 크기로 떴다가,
	 * 목록이 도착하는 순간 화면 높이만큼 부풀었다. 사용자에겐 **작은 창이 떴다가 큰 창이
	 * 덮는 것처럼** 보였다. 자리를 미리 잡아 두면 창이 한 번만 열린다.
	 *
	 * 개수는 어차피 모르니 두 줄(10개)만 깐다 — 실제가 더 많으면 조금 늘어나고 적으면 줄지만,
	 * 손바닥에서 전체 화면으로 뛰던 것과는 다르다.
	 */
	renderMonSkeletons(n = 10) {
		const out = [];
		for (let i = 0; i < n; i++) {
			out.push(<span class="cwm-mon is-skel" key={`skel${i}`} aria-hidden>
				<span class="cwm-skel-img"></span>
				<span class="cwm-skel-line"></span>
				<span class="cwm-skel-line is-short"></span>
			</span>);
		}
		return out;
	}

	renderMonCard(mon: CwmMon, opts: { locked?: string | null, picked?: boolean, notAtHome?: boolean, onClick?: () => void } = {}) {
		const locked = opts.locked ?? null;
		return <button
			type="button"
			class={`cwm-mon${opts.picked ? ' is-picked' : ''}${locked ? ' is-locked' : ''}`}
			disabled={!!locked || !opts.onClick}
			onClick={opts.onClick}
		>
			{/*
				「우리 집에 없는 아이」 (2026-09-30 사용자 확정 — 배지만, 정렬·필터 없음).
				유저끼리 키우는 종이 겹쳐 보여서, 없는 계열 쪽으로 눈이 가게 한다. 판정은 서버가
				내 아이 목록만 보고 한다 (pokemon-market.ts notAtHomeOf).
			*/}
			{opts.notAtHome ? <span class="cwm-home-tag">우리 집에 없는 아이</span> : null}
			<img src={cwmSprite(mon)} data-cwfb={cwmSpriteFb(mon)} onError={cwmImgFb} alt="" />
			<b>{cwmMonName(mon)}{mon.shiny ? ' ✨' : ''}</b>
			{cwmSpeciesLabel(mon) ? <span class="cwm-mon-species">{cwmSpeciesLabel(mon)}</span> : null}
			<span class="cwm-mon-sub">
				{mon.abilityKo}{mon.abilityHidden ? ' (숨은 특성)' : ''}
			</span>
			{mon.bagCount ? <span class="cwm-mon-sub">도구 {mon.bagCount}개 함께</span> : null}
			{/*
				2026-09-22: 전에는 「XX님이 지어준 이름이에요」였는데, `originalOwnerName` 은
				**첫 주인**이지 이름을 지어준 사람이 아니다 (showdown-pokemon.ts attachOwnerLabels).
				이름을 안 지어준 아이에도 그 문구가 떠서 거짓말이 됐다 (사용자 지적).
				누가 첫 보호자였는지만 말한다.

				2026-09-23: 「처음 주인」 → 「첫 보호자」, 「님」도 뺐다 (사용자 지시). 아이가 오가는
				자리의 어휘는 소유가 아니라 보호다 — 경매를 「입양」으로 부르는 것과 같은 결이다.
			*/}
			{mon.originalOwnerName
				? <span class="cwm-mon-credit">첫 보호자 {mon.originalOwnerName}</span> : null}
			{locked ? <span class="cwm-mon-lock">{CWM_LOCK_KO[locked] || '지금은 안 돼요'}</span> : null}
		</button>;
	}

	/** 내가 응모한 분양 글 + 결과. 「내 글」 탭 아래쪽. */
	renderMyEntries() {
		const rows = this.me?.myEntries ?? [];
		if (!rows.length) return null;
		return <>
			<h3 class="cwm-sub-h">내가 응모한 아이</h3>
			<div class="cwm-entries">
				{rows.map(e => <div class={`cwm-entry is-${e.outcome}`} key={e.listingId}>
					{e.mon ? <img class="cwm-entry-img" src={cwmSprite(e.mon)} data-cwfb={cwmSpriteFb(e.mon)} onError={cwmImgFb} alt="" /> : <span class="cwm-entry-img" />}
					<span class="cwm-entry-body">
						<b>{e.mon ? cwmMonName(e.mon) : '이름을 알 수 없는 아이'}</b>
						<small>{e.ownerName}님의 아이</small>
					</span>
					<span class="cwm-entry-state">
						{CWM_ENTRY_KO[e.outcome]}
						{e.outcome === 'waiting' && e.dueInMs !== null
							? <small>{this.giveLeftText(e.dueInMs)}</small> : null}
						{e.outcome === 'won' && e.dueInMs !== null
							? <small>{this.claimLeftText(e.dueInMs)}</small> : null}
						{e.outcome === 'queued' && e.drawRank !== null
							? <small>내 순번 {e.drawRank}번</small> : null}
					</span>
				</div>)}
			</div>
		</>;
	}

	renderListing(l: CwmListing) {
		const want = l.want;
		return <div class={`cwm-card${l.anyOfferReady ? '' : ' is-quiet'}`} key={l.id}>
			<div class="cwm-card-head">
				<img class="cwm-want-img" src={want ? cwmSpeciesSprite(want.spriteId) : ''} data-cwfb={want ? cwmDot(want.spriteId, false) : ''} onError={cwmImgFb} alt="" />
				<div class="cwm-want">
					<span class="cwm-eyebrow">{l.ownerName}님이 찾고 있어요</span>
					<strong>{want?.ko}</strong>
					<span class="cwm-want-cond">
						{want && want.abilitiesKo.length ? `특성 ${want.abilitiesKo.join(', ')}` : '어떤 특성이어도 괜찮아요'}
						{want?.shinyOnly ? ', 반짝이는 아이만' : ''}
						{want?.genderKo ? `, ${want.genderKo}만` : ''}
					</span>
					{/*
						조건이 아니라 **문을 넓히는** 줄이라 조건 줄에 섞지 않고 따로 세운다 (2026-09-23).
						조상 이름을 그대로 늘어놓는다 — 「진화 전」만으로는 어디까지인지 모른다.
					*/}
					{want?.includePrevo ? <span class="cwm-want-prevo">
						{want.prevoKo && want.prevoKo.length
							? `${want.prevoKo.join(' · ')}도 괜찮아요`
							: '진화 전 아이도 괜찮아요'}
					</span> : null}
				</div>
			</div>
			<div class="cwm-offer-label">이 중에 한 명을 데려갈 수 있어요</div>
			<div class="cwm-offers">
				{l.offers.map(o => this.renderMonCard(o.mon, { locked: o.lockReason, notAtHome: o.notAtHome }))}
			</div>
			<div class="cwm-card-foot">
				{l.blockedOnly
					? <span class="cwm-hint">어린이집에서 데려오면 보낼 수 있어요</span>
					: l.iCanMatch ? <span class="cwm-hint is-good">보낼 수 있는 아이가 있어요</span> : <span />}
				{l.mine
					? <button class="cwm-btn is-quiet" disabled={this.busy} onClick={() => this.cancelListing(l.id)}>내리기</button>
					: <button class="cwm-btn" disabled={!l.anyOfferReady || !l.iCanMatch} onClick={() => this.openAccept(l)}>데려가기</button>}
			</div>
		</div>;
	}

	renderCompose() {
		const sp = this.pickedSpecies;
		const list = this.species ?? [];
		const hits = this.query ? list.filter(s => cwmMatches(s, this.query)).slice(0, 8) : [];
		const max = this.me?.offerMax ?? 10;
		return <div class="cwm-dialog">
			<div class="cwm-dialog-in">
				<h3>어떤 아이를 찾고 있나요</h3>
				{!sp ? <>
					<input
						class="cwm-search"
						type="text"
						value={this.query}
						placeholder="이름을 적어보세요 (ㅍㅋㅊ 도 돼요)"
						onInput={this.onQuery}
					/>
					{!this.species ? <p class="cwm-note">아이들을 불러오는 중이에요.</p> : null}
					<div class="cwm-chips">
						{hits.map(s => <button type="button" class="cwm-chip" key={s.id} onClick={() => this.pickSpecies(s)}>
							<img src={cwmSpeciesSprite(s.spriteId || s.id)} data-cwfb={cwmDot(s.spriteId || s.id, false)} onError={cwmImgFb} alt="" />
							<span>{s.ko}</span>
						</button>)}
					</div>
					{this.query && this.species && !hits.length
						? <p class="cwm-note">그 이름을 못 찾았어요. 아직 이 서버에 없는 아이일 수도 있어요.</p> : null}
				</> : <>
					<div class="cwm-picked">
						<img src={cwmSpeciesSprite(sp.spriteId || sp.id)} data-cwfb={cwmDot(sp.spriteId || sp.id, false)} onError={cwmImgFb} alt="" />
						<strong>{sp.ko}</strong>
						<button type="button" class="cwm-btn is-quiet" onClick={() => { this.pickedSpecies = null; this.forceUpdate(); }}>다시 고르기</button>
					</div>
					<div class="cwm-conds">
						<label class="cwm-check">
							<input type="checkbox" checked={!this.anyAbilityPicked()} onChange={() => { this.pickedAbilities = {}; this.forceUpdate(); }} />
							<span>어떤 특성이어도 괜찮아요</span>
						</label>
						{sp.abilities.map(a => <label class="cwm-check" key={a.id}>
							<input type="checkbox" checked={!!this.pickedAbilities[a.id]} onChange={() => this.toggleAbility(a.id)} />
							<span>{a.ko}{a.hidden ? ' (숨은 특성)' : ''}</span>
						</label>)}
						{sp.hasPrevo ? <label class="cwm-check">
							<input type="checkbox" checked={this.wantIncludePrevo} onChange={() => { this.wantIncludePrevo = !this.wantIncludePrevo; this.forceUpdate(); }} />
							<span>진화 전 아이도 괜찮아요</span>
						</label> : null}
						<label class="cwm-check">
							<input type="checkbox" checked={this.wantShiny} onChange={() => { this.wantShiny = !this.wantShiny; this.forceUpdate(); }} />
							<span>반짝이는 아이만</span>
						</label>
						<label class="cwm-check">
							<input type="checkbox" checked={this.wantGender === 'F'} onChange={() => { this.wantGender = this.wantGender === 'F' ? null : 'F'; this.forceUpdate(); }} />
							<span>여자아이만</span>
						</label>
						<label class="cwm-check">
							<input type="checkbox" checked={this.wantGender === 'M'} onChange={() => { this.wantGender = this.wantGender === 'M' ? null : 'M'; this.forceUpdate(); }} />
							<span>남자아이만</span>
						</label>
					</div>
					{/*
						켰을 때만 한 줄 붙인다 (2026-09-23) — 안 적으면 「특성을 골랐는데 왜 랄토스가
						맞는 아이로 뜨지」가 된다. 특성은 **자란 뒤 기준**으로 대조한다.
					*/}
					{this.wantIncludePrevo ? <p class="cwm-note">
						진화 전 아이까지 받아요. 특성은 <b>다 자란 뒤 기준</b>으로 맞춰 봐요.
						자라도 여기까지 못 오는 아이(성별로 갈리는 진화)는 빼 둬요.
					</p> : null}

					<h3>보내줄 수 있는 아이 <small>{this.offerCount()} / {max}</small></h3>
					<p class="cwm-note">
						고른 아이 중 <b>한 명만</b> 새 집으로 가요. 가방 도구도 함께 갑니다.
						한 번 인연이 맺어지면 이름은 지금 이름 그대로 남아요.
					</p>
					<div class="cwm-offers is-pick">
						{!this.monOptions ? this.renderMonSkeletons() : this.monOptions.map(o => this.renderMonCard(o.mon, {
							locked: o.lockReason,
							picked: !!this.pickedOffers[o.mon.id],
							onClick: () => this.toggleOffer(o.mon.id),
						}))}
					</div>
				</>}
				{this.error ? <p class="cwm-error" role="alert">{this.error}</p> : null}
				<div class="cwm-dialog-foot">
					<button class="cwm-btn is-quiet" onClick={this.closeCompose}>그만두기</button>
					<button class="cwm-btn" disabled={!sp || this.busy || !this.offerCount()} onClick={this.submitListing}>
						올리기{this.me?.listingFee ? ` (체리 ${this.me.listingFee.toLocaleString()})` : ''}
					</button>
				</div>
			</div>
		</div>;
	}

	anyAbilityPicked(): boolean {
		for (const k in this.pickedAbilities) if (this.pickedAbilities[k]) return true;
		return false;
	}

	renderAccept() {
		const l = this.acceptFor!;
		const takeable = l.offers.filter(o => !o.lockReason);
		const givable = this.giveCandidates(l);
		return <div class="cwm-dialog">
			<div class="cwm-dialog-in">
				<h3>어떤 아이를 데려올까요</h3>
				<div class="cwm-offers is-pick">
					{takeable.map(o => this.renderMonCard(o.mon, {
						notAtHome: o.notAtHome,
						picked: this.acceptTake === o.mon.id,
						onClick: () => { this.acceptTake = o.mon.id; this.forceUpdate(); },
					}))}
				</div>
				<h3>{l.ownerName}님께 보낼 아이</h3>
				{this.monOptions && !givable.length
					? <p class="cwm-note">지금 보낼 수 있는 아이가 없어요.</p> : null}
				<div class="cwm-offers is-pick">
					{!this.monOptions ? this.renderMonSkeletons(5) : givable.map(o => this.renderMonCard(o.mon, {
						locked: o.lockReason,
						picked: this.acceptGive === o.mon.id,
						onClick: () => { this.acceptGive = o.mon.id; this.forceUpdate(); },
					}))}
				</div>
				<p class="cwm-note">
					보낸 아이는 그 집 아이가 되고, 데려온 아이는 우리 집 아이가 돼요.
				</p>
				<p class="cwm-notice">{cwmTakeNotice()}</p>
				{this.error ? <p class="cwm-error" role="alert">{this.error}</p> : null}
				<div class="cwm-dialog-foot">
					<button class="cwm-btn is-quiet" onClick={this.closeAccept}>그만두기</button>
					<button class="cwm-btn" disabled={!this.acceptTake || !this.acceptGive || this.busy} onClick={this.submitAccept}>
						인연 맺기
					</button>
				</div>
			</div>
		</div>;
	}

	renderDone() {
		const d = this.done!;
		return <div class="cwm-dialog">
			<div class="cwm-dialog-in is-done">
				<h3>새 인연이 맺어졌어요</h3>
				<div class="cwm-done-row">
					<div>
						<span class="cwm-eyebrow">우리 집에 온 아이</span>
						<img src={cwmSprite(d.received)} data-cwfb={cwmSpriteFb(d.received)} onError={cwmImgFb} alt="" />
						<b>{cwmMonName(d.received)}</b>
					</div>
					{d.sent ? <div>
						<span class="cwm-eyebrow">새 집으로 간 아이</span>
						<img src={cwmSprite(d.sent)} data-cwfb={cwmSpriteFb(d.sent)} onError={cwmImgFb} alt="" />
						<b>{cwmMonName(d.sent)}</b>
					</div> : null}
				</div>
				<div class="cwm-dialog-foot">
					<button class="cwm-btn" onClick={() => { this.done = null; this.forceUpdate(); }}>고마워요</button>
				</div>
			</div>
		</div>;
	}

	override render() {
		const narrow = this.props.narrow;
		const me = this.me;
		const listings = this.listings ?? [];
		const mine = me?.myListing ?? null;
		const shown = this.tab === 'mine' ? (mine ? [mine] : []) : listings.filter(l => !l.mine);

		return <div class="cwm-wrap">
			<style>{CWM_CSS}</style>
			<div class={`cwm-root${narrow ? ' is-narrow' : ''}`}>
				<div class="cwm-head">
					<div>
						<span class="cwm-eyebrow">인연의 보금자리</span>
						<h2>아이를 찾고, 아이를 보내요</h2>
					</div>
					<button class="cwm-btn is-quiet" disabled={this.loading} onClick={this.refresh}>새로고침</button>
				</div>

				{me && !me.open ? <div class="cwm-empty">
					<p>아직 문을 열지 않았어요. 곧 만나요!</p>
				</div> : <>
					<div class="cwm-tabs">
						<div class="cwm-tabs-main">
							{me?.giveOpen ? <button class={`cwm-tab-main${this.tab === 'give' ? ' is-on' : ''}`}
								onClick={() => this.pickTab('give')}>
								<span class="ico" aria-hidden={true}>🎁</span>
								<b>무료분양</b>
							</button> : null}
							<button class={`cwm-tab-main${this.tab === 'browse' ? ' is-on' : ''}`}
								onClick={() => this.pickTab('browse')}>
								<span class="ico" aria-hidden={true}>🔁</span>
								<b>서로 보내주기</b>
							</button>
						</div>
						<div class="cwm-tabs-sub">
							<button class={`cwm-tab-sub${this.tab === 'mine' ? ' is-on' : ''}`}
								onClick={() => this.pickTab('mine')}>내 글</button>
							{me?.acceptsLeft !== null && me?.acceptsLeft !== undefined
								? <span class="cwm-left">오늘 {me.acceptsLeft}번 더 만날 수 있어요</span> : null}
						</div>
					</div>

					{this.tab === 'browse' ? <label class={`cwm-filter${this.matchable ? ' is-on' : ''}`}>
						<input type="checkbox" checked={this.matchable} onChange={() => {
							this.matchable = !this.matchable;
							this.loadedAt = 0;
							this.refresh();
						}} />
						<span>우리 집 아이를 찾는 글만 보기</span>
						<small>{this.matchable ? '지금은 보낼 수 있는 글만 보여요' : '체크하면 보낼 수 있는 글만 골라 봐요'}</small>
					</label> : null}

					{this.tab === 'mine' && !mine ? <div class="cwm-empty">
						<p>아직 올린 글이 없어요.</p>
						<button class="cwm-btn" onClick={this.openCompose}>글 올리기</button>
					</div> : null}
					{this.tab === 'mine' && mine
						? <p class="cwm-note">글은 한 번에 하나만 올릴 수 있어요. 한 명이 새 집을 찾으면 글은 닫혀요.</p> : null}
					{this.tab === 'mine' ? this.renderMyEntries() : null}

					{this.error && !this.composing && !this.acceptFor
						? <p class="cwm-error" role="alert">{this.error}</p> : null}

					{this.loading && !this.listings ? <p class="cwm-note">보금자리를 둘러보는 중이에요.</p> : null}

					{this.tab === 'give' ? <>
						<p class="cwm-note">
							대가 없이 새 가족을 찾아주는 자리예요. 24시간 동안 응모를 받아 추첨하고,
							아무도 없으면 그때부터 누구나 데려갈 수 있어요.
							<b> 응모는 한 번에 한 아이만</b> 할 수 있어요. 발표가 나면 다음 아이에게 응모할 수 있어요.
						</p>
						{/* 남은 응모 수는 **누르기 전에** 보여야 한다 — 없으면 상한에 닿는 그 클릭에서야 벽을 만난다. */}
						{me?.entriesLeft !== null && me?.entriesLeft !== undefined ? <p class="cwm-note">
							{me.entriesLeft > 0
								? `오늘 ${me.entriesLeft}곳에 더 응모할 수 있어요.`
								: '오늘 응모는 여기까지예요. 내일 다시 만나요!'}
						</p> : null}
						<p class="cwm-notice">{cwmTakeNotice()}</p>
						<div class="cwm-list">{(this.gives ?? []).map(g => this.renderGive(g))}</div>
						{!this.loading && !(this.gives ?? []).length
							? <div class="cwm-empty"><p>아직 새 가족을 찾는 아이가 없어요.</p></div> : null}
						<div class="cwm-cta">
							<button class="cwm-btn" onClick={this.openGiveCompose}>우리 아이 새 가족 찾아주기</button>
						</div>
					</> : <div class="cwm-list">{shown.map(l => this.renderListing(l))}</div>}

					{this.tab === 'browse' && !this.loading && !shown.length ? <div class="cwm-empty">
						<p>{this.matchable
							? '우리 집 아이를 찾는 글이 아직 없어요.'
							: '아직 올라온 글이 없어요.'}</p>
						{this.matchable
							? <button class="cwm-btn is-quiet" onClick={() => { this.matchable = false; this.loadedAt = 0; this.refresh(); }}>전부 보기</button>
							: null}
					</div> : null}

					{this.tab === 'browse' && !mine ? <div class="cwm-cta">
						<button class="cwm-btn" onClick={this.openCompose}>나도 글 올리기</button>
					</div> : null}
				</>}

				{this.composing ? this.renderCompose() : null}
				{this.givingUp ? this.renderGiveCompose() : null}
				{this.acceptFor ? this.renderAccept() : null}
				{this.done ? this.renderDone() : null}
			</div>
		</div>;
	}
}

/**
 * 배틀홈 입구 — **아무것도 묻지 않는다**.
 *
 * 「지금 글이 몇 개 있어요」를 붙이고 싶어지지만, 그러려면 홈에 통로가 하나 더 생긴다.
 * 홈은 이미 여러 조각이 얹혀 있는 자리라 거기에 요청을 더하면 사람 하나가 화면 수만큼
 * 요청을 곱하는 그 구조로 돌아간다 (2026-09-19 관전 띠 사고). 그래서 이 카드는 **정적**이고,
 * 셀 것이 필요하면 방 안에서 센다.
 */
/**
 * 스위치 확인 결과를 **모듈 하나**가 들고 있는다.
 *
 * 보금자리 탭이 「인연의 보금자리」 하위탭을 그릴지 정하는 데 쓴다. 탭이 다시 그려질 때마다
 * 물으면 요청이 화면 수만큼 늘어나므로 한 번 받아서 여기 둔다. 타이머는 없다 — 어드민이
 * 스위치를 켠 뒤 유저가 새로고침하면 그때 반영된다(오픈은 한 번 있는 일이라 그걸로 충분하다).
 *
 * ⚠️ 꺼져 있으면 **하위탭 자체가 없다.** 들어가서 「아직 문을 열지 않았어요」를 보는 건
 * 노출이다 — 배포는 조용해야 하고, 어드민이 켜는 순간부터 보이기 시작해야 한다.
 * 서버 라우트도 같은 스위치로 `/market/me` 말고는 전부 막혀 있다.
 */
let cwmGateKnown: boolean | null = null;
let cwmGateAsking = false;
const cwmGateWaiters: (() => void)[] = [];

/** 지금까지 알아낸 값. null = 아직 모름 → 하위탭을 그리지 않는다. */
export function cwmMarketOpen(): boolean | null {
	return cwmGateKnown;
}

/**
 * 상단 「보금자리」 탭의 NEW 점 (2026-09-22).
 *
 * ## 왜
 *
 * 인연의 보금자리는 **입구가 이 탭 하나뿐**인데, 그 탭은 09-21 에 생겼고 그때 안에는 경매만
 * 있었다. 기존 유저는 「보금자리 탭 = 특별한 보금자리」로 학습을 끝냈으니, 안에 새 방이
 * 생겨도 다시 열어 볼 이유가 없다. 점 하나가 「여기 뭔가 생겼다」를 말한다.
 *
 * ## 규칙 셋 ⚠️
 *
 *  - **숫자를 붙이지 않는다.** 「글 3개」를 적으려면 주기적으로 세야 하고, 그 순간 홈에
 *    통로가 하나 더 생긴다 (통로 하나 규약 §4-6). 점은 정적이라 요청이 0이다.
 *  - **스위치가 꺼져 있으면 점도 없다** — 게이트 결과가 참일 때만 뜬다. 오픈 전 노출 0.
 *  - **한 번 열어 보면 영영 사라진다.** 날짜로 내리면 그날 처음 온 사람이 못 보고,
 *    안 내리면 본 사람에게 영원히 붙어 있다. 기준은 「봤나」다.
 *
 * 브라우저마다 따로 기억한다(localStorage) — 기기별로 한 번씩 뜨는 건 의도다.
 */
/** 판 번호를 넣는다 — 온실 리모델링(2026-10-05)처럼 알릴 일이 생기면 올려서 본 사람에게도 점을 다시 띄운다. */
const CWM_SEEN_KEY = 'cherish-nest-seen-2';

/** 보금자리 탭을 열었다 — 점을 내린다. */
export function cwmNestSeen(): void {
	try { localStorage.setItem(CWM_SEEN_KEY, '1'); } catch {}
}

/** 지금 점을 그려야 하나. 아직 스위치를 모르면 **안 그린다** (없는 걸 있다고 그리지 않는다). */
export function cwmNestIsNew(): boolean {
	try { if (localStorage.getItem(CWM_SEEN_KEY)) return false; } catch { return false; }
	return cwmGateKnown === true;
}

export function cwmAskGate(onDone: () => void): void {
	if (cwmGateKnown !== null) return;
	cwmGateWaiters.push(onDone);
	if (cwmGateAsking) return;
	cwmGateAsking = true;
	fetch(`${CWM_API}/me`, { credentials: 'include' })
		.then(r => r.json())
		.then(j => {
			cwmGateKnown = j?.data?.open === true;
			const box = j?.data?.loveInbox;
			cwmLoveInbox = box && typeof box.matchId === 'string' && /^\d+$/.test(box.matchId) ? box : null;
		})
		// 로그인 전·비서버원·통신 실패는 전부 "안 보임" 쪽으로 — 없는 걸 있다고 그리지 않는다
		.catch(() => { cwmGateKnown = false; })
		.then(() => {
			cwmGateAsking = false;
			const waiters = cwmGateWaiters.splice(0, cwmGateWaiters.length);
			for (const fn of waiters) fn();
			cwmLoveBoot();
		});
}

// ---- 받은 온실 신청 · 신청 카드 링크 (0460, 2026-10-08, 계획서 온실.md §15) ------------------
//
// 선배가 통화방에 올린 카드 링크(`/cherish-nest?go=love&m=<요청>&src=card`)로 오면 그 신청 화면으로,
// 그냥 들어왔어도 답할 차례인 신청이 있으면 팝업 하나. 둘 다 **이미 있던 게이트 한 번**에 실려 온다
// (`/market/me` 의 loveInbox) — 요청을 새로 만들지 않는다(단면도 §4-6 통로 하나).
// 온실 화면(cherish-love.tsx)에는 `window.cherishLoveIntent`(요청 id) 하나로만 넘긴다 — 두 파일이 서로를
// import 하지 않게. 보금자리 패널(patch-nest-tab.py)이 그 값을 보고 온실 하위탭을 연다.
// 링크는 로그인 왕복을 거쳐도 산다: cherish-go-entry(patch-home-modes.py)가 쿼리를 10분 맡아 둔다.

interface CwmLoveInbox { matchId: string; fromName: string; count: number }
let cwmLoveInbox: CwmLoveInbox | null = null;
let cwmLoveBooted = false;
/** 「나중에」·「보러 가기」를 누른 신청 — 같은 신청으로는 다시 안 띄운다. */
const CWM_LOVE_INBOX_SEEN_KEY = 'cherish-love-inbox-seen';

function cwmOpenLove(matchId: string): void {
	(window as any).cherishLoveIntent = matchId;
	PS.join('cherish-nest' as RoomID);
	PS.update();
}

function cwmLoveBoot(): void {
	if (cwmLoveBooted) return;
	cwmLoveBooted = true;
	try {
		const qs = new URLSearchParams((window as any).cherishGoQuery || '');
		const m = qs.get('m') || '';
		if (qs.get('go') === 'love' && /^\d{1,19}$/.test(m)) {
			cwmMarkInboxSeen(m);
			cwmOpenLove(m);
			return;
		}
	} catch {}
	const box = cwmLoveInbox;
	if (!box) return;
	try { if (localStorage.getItem(CWM_LOVE_INBOX_SEEN_KEY) === box.matchId) return; } catch { return; }
	cwmShowLoveInbox(box);
}

function cwmMarkInboxSeen(matchId: string): void {
	try { localStorage.setItem(CWM_LOVE_INBOX_SEEN_KEY, matchId); } catch {}
}

/**
 * 팝업은 DOM 으로 직접 — 부팅 때 그릴 자리(방)가 아직 없다. 이름은 textContent 로만 넣는다(사람이 지은 문자열).
 * 색은 스킨 토큰(--cw-*) — 보금자리 외형 규약과 같다.
 */
function cwmShowLoveInbox(box: CwmLoveInbox): void {
	const back = document.createElement('div');
	back.setAttribute('style', 'position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:16px');
	const card = document.createElement('div');
	card.setAttribute('style', 'width:min(380px,100%);border-radius:16px;background:var(--cw-surface,#fff);color:var(--cw-ink,#222);padding:18px 18px 14px;box-shadow:0 12px 40px rgba(0,0,0,.3);font-size:14px;line-height:1.55');
	const title = document.createElement('b');
	title.setAttribute('style', 'display:block;font-size:16px;margin-bottom:8px');
	title.textContent = '🌷 온실 신청이 왔어요';
	const body = document.createElement('p');
	body.setAttribute('style', 'margin:0 0 6px');
	body.textContent = `${box.fromName}님이 체리의 온실에서 함께 아이를 키우고 싶어 해요.`;
	card.appendChild(title);
	card.appendChild(body);
	if (box.count > 1) {
		const more = document.createElement('p');
		more.setAttribute('style', 'margin:0 0 6px;opacity:.7;font-size:12px');
		more.textContent = `받은 신청이 ${box.count}건 있어요.`;
		card.appendChild(more);
	}
	const row = document.createElement('div');
	row.setAttribute('style', 'display:flex;gap:8px;justify-content:flex-end;margin-top:12px');
	const later = document.createElement('button');
	later.className = 'button';
	later.textContent = '나중에';
	const go = document.createElement('button');
	go.className = 'button';
	go.setAttribute('style', 'font-weight:700');
	go.textContent = '보러 가기';
	const close = () => { cwmMarkInboxSeen(box.matchId); back.remove(); };
	later.onclick = close;
	back.onclick = (e: MouseEvent) => { if (e.target === back) close(); };
	go.onclick = () => { close(); cwmOpenLove(box.matchId); };
	row.appendChild(later);
	row.appendChild(go);
	card.appendChild(row);
	back.appendChild(card);
	document.body.appendChild(back);
}

const CWM_CSS = `
/* ── 인연의 보금자리 외형 (2026-09-22 개편) ────────────────────────────────
   처음엔 자주(#8a5a76) 한 색으로 짰는데, 이 사이트 어디에도 없는 색이라 화면만 혼자
   칙칙하게 떠 있었다 (사용자 지적). 지금은 스킨 토큰(--cw-*)만 쓴다 —
   라이트·하늘(data-cw-theme="sky")·다크 세 테마가 공짜로 따라오고, 바로 위 앱바 알약과
   같은 색이 된다. ⚠️ 새 색을 여기서 직접 만들지 말 것. 토큰에 없으면 스킨에 먼저 넣는다.
   ⚠️ .cwm-dialog 가 position:fixed 다 — 조상(.cwm-root 등)에 filter·backdrop-filter·
   transform 을 주면 기준이 그 조상으로 바뀌어 화면 밖으로 샌다. 카드에만 쓸 것. */
.cwm-root{padding:16px;max-width:900px;margin:0 auto;font-size:13px;color:var(--cw-ink)}
.cwm-root.is-narrow{padding:12px 10px}
.cwm-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:14px}
.cwm-head h2{margin:2px 0 0;font-size:21px;font-weight:800;letter-spacing:-.02em;color:var(--cw-ink)}
.cwm-eyebrow{display:block;font-size:10px;letter-spacing:.14em;color:var(--cw-ink-muted);text-transform:uppercase}
/* 탭 — 주 탭 둘(무료분양·서로 보내주기)을 **같은 크기의 큰 알약**으로 나란히 세운다
   (2026-09-23 사용자 지시). 셋을 같은 작은 알약으로 늘어놓았더니 「무료분양」이 있는 줄도
   모르고 교환만 보고 나가는 사람이 있었다.
   ⚠️ 설명 한 줄을 밑에 달았다가 **뺐다** — 제목만으로 뜻이 다 전해지는데 두 줄이 되면
   알약이 커지고 글자만 많아진다. 크기와 색이 이미 「여기가 주 통로다」를 말한다.
   「내 글」은 성격이 다르다(둘러보는 자리가 아니라 내 것 관리) — 작은 알약으로 아래 줄에 둔다. */
.cwm-tabs{display:flex;flex-direction:column;gap:9px;margin-bottom:12px}
.cwm-tabs-main{display:flex;gap:10px;flex-wrap:wrap}
.cwm-tab-main{flex:1 1 170px;display:flex;align-items:center;justify-content:center;gap:9px;
 padding:13px 16px;border-radius:14px;border:1.5px solid var(--cw-line-strong);background:var(--cw-tint);
 color:var(--cw-ink-soft);cursor:pointer;font-family:inherit;transition:.16s ease}
.cwm-tab-main:hover{border-color:var(--cw-accent);background:var(--cw-surface)}
.cwm-tab-main.is-on{background:var(--cw-cta-grad);border-color:transparent;color:var(--cw-on-accent);
 box-shadow:var(--cw-cta-shadow),var(--cw-cta-inset)}
.cwm-tab-main .ico{font-size:19px;line-height:1;flex:none}
.cwm-tab-main b{font-size:15px;font-weight:700;letter-spacing:-.01em}
.cwm-tabs-sub{display:flex;align-items:center;gap:6px}
.cwm-tab-sub{padding:7px 14px;border-radius:999px;border:1px solid var(--cw-line);
 background:var(--cw-surface);color:var(--cw-ink-muted);font-size:12px;font-weight:600;
 cursor:pointer;font-family:inherit;transition:.16s ease}
.cwm-tab-sub:hover{border-color:var(--cw-line-strong);background:var(--cw-tint)}
.cwm-tab-sub.is-on{background:var(--cw-cta-grad);border-color:transparent;color:var(--cw-on-accent);
 box-shadow:var(--cw-cta-shadow),var(--cw-cta-inset)}
.cwm-left{margin-left:auto;font-size:11px;color:var(--cw-ink-muted)}
/* 거르는 칸 — 기본이 꺼짐이라 **있는 줄 모르고 지나치기 쉽다**. 그래서 알약을 또렷하게
   세우고(강조 테두리 + 틴트), 켜고 끌 때 무엇이 달라지는지 작은 글씨로 같이 말해 준다.
   켜면 탭 알약과 같은 강조색으로 채워 「지금 걸러 보는 중」이 한눈에 보이게 한다. */
.cwm-filter{display:inline-flex;align-items:center;gap:8px;margin-bottom:12px;padding:8px 16px 8px 12px;
 border-radius:999px;border:1.5px solid var(--cw-line-strong);background:var(--cw-tint);
 font-size:12px;font-weight:600;color:var(--cw-ink-soft);cursor:pointer;transition:.16s ease}
.cwm-filter:hover{background:var(--cw-tint-2)}
.cwm-filter small{font-weight:400;opacity:.75;font-size:11px}
.cwm-filter input{accent-color:var(--cw-accent);width:15px;height:15px}
.cwm-filter.is-on{background:var(--cw-cta-grad);border-color:transparent;color:var(--cw-on-accent);
 box-shadow:var(--cw-cta-shadow),var(--cw-cta-inset)}
.cwm-filter.is-on:hover{background:var(--cw-cta-grad)}
.cwm-filter.is-on small{opacity:.85}
.cwm-filter.is-on input{accent-color:var(--cw-white)}
.cwm-list{display:flex;flex-direction:column;gap:12px}
.cwm-card{padding:15px;border-radius:var(--cw-r);background:var(--cw-surface);border:0;
 box-shadow:var(--cw-card-shadow);transition:box-shadow .18s ease,transform .18s ease}
.cwm-card:hover{transform:translateY(-1px);box-shadow:var(--cw-card-shadow),var(--cw-cta-glow)}
.cwm-card.is-quiet{opacity:.62}
.cwm-card.is-quiet:hover{transform:none;box-shadow:var(--cw-card-shadow)}
.cwm-card-head{display:flex;align-items:center;gap:12px;margin-bottom:10px}
.cwm-want-img{width:56px;height:56px;image-rendering:auto;flex:none}
.cwm-want{display:flex;flex-direction:column;gap:2px;min-width:0}
.cwm-want strong{font-size:16px;font-weight:700;color:var(--cw-ink)}
.cwm-want-cond{font-size:11px;color:var(--cw-ink-muted)}
/* 「진화 전 아이도 괜찮아요」 — 조건이 아니라 문을 넓히는 줄이라 조건과 색을 달리 한다
   (2026-09-23). 강조색으로 채우면 경고로 읽히므로 옅은 띠에 본문색만 얹는다. */
.cwm-want-prevo{display:inline-block;margin-top:3px;padding:1px 7px;border-radius:999px;
 background:var(--cw-tint);border:1px solid var(--cw-line);font-size:10px;color:var(--cw-ink-soft)}
.cwm-offer-label{font-size:11px;color:var(--cw-ink-muted);margin-bottom:6px}
.cwm-offers{display:grid;grid-template-columns:repeat(auto-fill,minmax(104px,1fr));gap:8px}
.cwm-mon{display:flex;flex-direction:column;align-items:center;gap:2px;padding:8px 6px;border-radius:var(--cw-r-sm);
 background:var(--cw-tint);border:1px solid var(--cw-line);cursor:default;text-align:center;font-family:inherit;
 transition:.16s ease}
.cwm-offers.is-pick .cwm-mon:not(.is-locked){cursor:pointer}
.cwm-offers.is-pick .cwm-mon:not(.is-locked):hover{border-color:var(--cw-line-strong);background:var(--cw-tint-2)}
.cwm-mon.is-picked{border-color:var(--cw-accent);background:var(--cw-tint-2);
 box-shadow:0 0 0 2px color-mix(in srgb, var(--cw-accent) 30%, transparent)}
.cwm-mon.is-locked{opacity:.5}
/* 자리표 — 목록이 오기 전 창 크기를 미리 잡아 둔다 (2026-09-22, 위 renderMonSkeletons 참고).
   버튼이 아니라 span 이라 탭 순서에 안 걸리고, aria-hidden 이라 읽어 주지도 않는다. */
.cwm-mon.is-skel{cursor:default;pointer-events:none;gap:6px;justify-content:center;min-height:104px}
.cwm-skel-img{width:52px;height:52px;border-radius:50%}
.cwm-skel-line{width:62px;height:9px;border-radius:999px}
.cwm-skel-line.is-short{width:40px;height:7px}
.cwm-skel-img,.cwm-skel-line{background:var(--cw-line);opacity:.55;animation:cwm-skel 1.25s ease-in-out infinite}
.cwm-skel-line.is-short{animation-delay:.15s}
@keyframes cwm-skel{0%,100%{opacity:.3}50%{opacity:.7}}
.cwm-mon img{width:52px;height:52px;object-fit:contain}
.cwm-mon b{font-size:12px;color:var(--cw-ink);max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwm-mon-sub{font-size:10px;color:var(--cw-ink-muted)}
/* 종족명 — 이름표 바로 밑. 별명이 걸린 아이만 뜬다 (cwmSpeciesLabel). */
.cwm-mon-species{font-size:10px;color:var(--cw-ink-soft);opacity:.9}
.cwm-mon-credit{font-size:9px;color:var(--cw-ink-muted);opacity:.85;line-height:1.3}
.cwm-mon-lock{font-size:10px;color:var(--cw-ink-soft)}
.cwm-card-foot{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:12px}
.cwm-hint{font-size:11px;color:var(--cw-ink-muted)}
/* 당첨자가 정해진 분양 글 — 제목 위 띠. 남의 당첨은 힘을 빼고, 내 당첨은 강조색 (2026-09-24). */
.cwm-claim-tag{align-self:flex-start;display:inline-block;margin:0 0 3px;padding:2px 8px;border-radius:999px;
 font-size:10px;font-weight:700;letter-spacing:.02em;color:var(--cw-ink-muted);background:var(--cw-tint);border:1px solid var(--cw-line-strong)}
.cwm-claim-tag.is-me{color:#fff;background:#e0525f;border-color:#e0525f}
/* 「우리 집에 없는 아이」 (2026-09-30) — 녹색 계열은 「보낼 수 있는 아이가 있어요」(is-good)와 같은 뜻의 결. */
.cwm-home-tag{align-self:center;display:inline-block;margin:0 0 3px;padding:1px 7px;border-radius:999px;
 font-size:9px;font-weight:700;white-space:nowrap;color:#4f8a68;background:rgba(95,156,120,.12);border:1px solid rgba(95,156,120,.45)}
.cwm-want .cwm-home-tag{align-self:flex-start;font-size:10px;padding:2px 8px}
.cwm-hint.is-good{color:#5f9c78;font-weight:600}
.cwm-btn{padding:9px 18px;border-radius:999px;border:0;background:var(--cw-cta-grad);color:var(--cw-on-accent);
 font-size:12px;font-weight:700;cursor:pointer;font-family:inherit;
 box-shadow:var(--cw-cta-shadow),var(--cw-cta-inset);transition:.16s ease}
.cwm-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:var(--cw-cta-shadow-hover),var(--cw-cta-inset)}
.cwm-btn:active:not(:disabled){transform:translateY(0)}
.cwm-btn:disabled{opacity:.45;cursor:default;box-shadow:none}
.cwm-btn.is-quiet{background:var(--cw-white);border:1px solid var(--cw-line-strong);color:var(--cw-ink-soft);box-shadow:none}
.cwm-btn.is-quiet:hover:not(:disabled){background:var(--cw-tint);box-shadow:none}
.cwm-note{font-size:11px;color:var(--cw-ink-muted);line-height:1.6;margin:8px 0}
/* 고지 — 되돌릴 수 없는 규칙이라 안내(cwm-note)보다 한 단 세게. 오류 띠와는 색이 다르다. */
.cwm-notice{font-size:11.5px;color:var(--cw-ink-soft);line-height:1.6;margin:10px 0;
 padding:9px 12px;border-radius:var(--cw-r-sm);background:var(--cw-tint);
 border-left:3px solid var(--cw-line-strong)}
/* 오류는 강조색(--cw-accent)과 같은 붉은 계열이라 글자색만으로는 안 갈린다 — 띠를 둘러 경고로 읽히게. */
.cwm-error{font-size:12px;color:#a8324e;background:#fff0f3;border:1px solid #f6c9d4;
 border-radius:var(--cw-r-sm);padding:9px 12px;margin:10px 0;line-height:1.5}
.cwm-empty{padding:30px 16px;text-align:center;color:var(--cw-ink-muted);display:flex;flex-direction:column;
 gap:12px;align-items:center;border-radius:var(--cw-r);background:var(--cw-tint);border:1px dashed var(--cw-line-strong)}
.cwm-cta{margin-top:16px;text-align:center}
/* 내가 응모한 아이 — 결과가 한눈에 갈려야 한다. 당첨은 강조색, 탈락은 힘을 뺀다. */
.cwm-sub-h{margin:20px 0 8px;font-size:14px;font-weight:700;color:var(--cw-ink)}
.cwm-entries{display:flex;flex-direction:column;gap:6px}
.cwm-entry{display:flex;align-items:center;gap:10px;padding:9px 12px;border-radius:var(--cw-r-sm);
 background:var(--cw-surface);border:1px solid var(--cw-line)}
.cwm-entry-img{width:40px;height:40px;flex:none;object-fit:contain}
.cwm-entry-body{display:flex;flex-direction:column;gap:1px;min-width:0;flex:1}
.cwm-entry-body b{font-size:13px;color:var(--cw-ink)}
.cwm-entry-body small{font-size:10px;color:var(--cw-ink-muted)}
.cwm-entry-state{display:flex;flex-direction:column;align-items:flex-end;gap:1px;
 font-size:11.5px;font-weight:600;color:var(--cw-ink-soft);text-align:right;flex:none}
.cwm-entry-state small{font-weight:400;font-size:10px;color:var(--cw-ink-muted)}
.cwm-entry.is-won{border-color:var(--cw-accent);background:var(--cw-tint-2)}
.cwm-entry.is-won .cwm-entry-state{color:var(--cw-accent-dark)}
.cwm-entry.is-lost,.cwm-entry.is-gone{opacity:.6}
.cwm-entry.is-taken .cwm-entry-state{color:#5f9c78}
.cwm-dialog{position:fixed;inset:0;background:rgba(58,18,33,.55);display:flex;align-items:center;
 justify-content:center;padding:16px;z-index:60}
.cwm-dialog-in{background:var(--cw-bg);border-radius:var(--cw-r);padding:20px;max-width:640px;width:100%;
 max-height:86vh;overflow-y:auto;box-shadow:var(--cw-card-shadow),0 24px 60px -20px rgba(0,0,0,.4)}
.cwm-dialog-in h3{margin:16px 0 8px;font-size:15px;font-weight:700;color:var(--cw-ink)}
.cwm-dialog-in h3:first-child{margin-top:0}
.cwm-dialog-in h3 small{font-weight:400;color:var(--cw-ink-muted);font-size:11px;margin-left:6px}
.cwm-search{width:100%;box-sizing:border-box;padding:11px 13px;border-radius:var(--cw-r-sm);
 border:1px solid var(--cw-line);background:var(--cw-white);color:var(--cw-ink);font-size:14px;font-family:inherit}
.cwm-search:focus{outline:0;border-color:var(--cw-line-strong);box-shadow:0 0 0 3px color-mix(in srgb, var(--cw-accent) 16%, transparent)}
.cwm-chips{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}
.cwm-chip{display:flex;align-items:center;gap:6px;padding:6px 12px 6px 6px;border-radius:999px;
 border:1px solid var(--cw-line);background:var(--cw-white);cursor:pointer;font-family:inherit;font-size:13px;
 color:var(--cw-ink);transition:.16s ease}
.cwm-chip:hover{border-color:var(--cw-line-strong);background:var(--cw-tint)}
.cwm-chip img{width:34px;height:34px;object-fit:contain}
.cwm-picked{display:flex;align-items:center;gap:10px;padding:11px;border-radius:var(--cw-r-sm);
 background:var(--cw-tint);border:1px solid var(--cw-line)}
.cwm-picked img{width:48px;height:48px;object-fit:contain}
.cwm-picked strong{flex:1;font-size:16px;font-weight:700;color:var(--cw-ink)}
.cwm-conds{display:flex;flex-direction:column;gap:7px;margin:12px 0}
.cwm-check{display:flex;align-items:center;gap:8px;font-size:13px;color:var(--cw-ink-soft);cursor:pointer}
.cwm-check input{accent-color:var(--cw-accent)}
.cwm-dialog-foot{display:flex;justify-content:flex-end;gap:8px;margin-top:18px}
.cwm-done-row{display:flex;gap:16px;justify-content:center;text-align:center;margin:12px 0}
.cwm-done-row img{width:76px;height:76px;object-fit:contain;display:block;margin:4px auto}
.cwm-root.is-narrow .cwm-offers{grid-template-columns:repeat(auto-fill,minmax(88px,1fr))}
.cwm-root.is-narrow .cwm-done-row{gap:10px}
/* 다크는 토큰이 뒤집혀 따라온다 — 예전엔 여기 한 벌을 더 손으로 적었는데, 색을 고칠 때마다
   두 군데를 맞춰야 해서 한쪽이 늘 뒤처졌다. 이제 토큰이 못 담는 것만 남긴다. */
html.cw-dark .cwm-dialog{background:rgba(9,6,18,.72)}
html.cw-dark .cwm-error{color:#ffb3c4;background:rgba(225,29,72,.12);border-color:rgba(251,113,133,.35)}
html.cw-dark .cwm-hint.is-good{color:#8fd0aa}
@media(prefers-reduced-motion:reduce){.cwm-entry,.cwm-card,.cwm-btn,.cwm-mon,.cwm-chip,.cwm-tab-main,.cwm-tab-sub,.cwm-filter{transition:none}
 .cwm-skel-img,.cwm-skel-line{animation:none}
 .cwm-card:hover,.cwm-btn:hover:not(:disabled){transform:none}}
`;
